<template>
  <!-- Mobile Card -->
  <RevCard class="mt-12 flex w-full flex-row items-center gap-8 p-16 md:hidden">
    <IconOfficialFilled size="32" />
    <div class="flex flex-col gap-4">
      <FormattedMessage
        class="body-2"
        :definition="translations.approvedSeller"
      >
        <template #rating>
          <span class="body-2-bold">
            {{
              i18n(translations.sellerRating, {
                rating: averageRate?.toPrecision(2),
                maxRating: MAX_RATING,
              })
            }}
          </span>
        </template>
      </FormattedMessage>

      <FormattedMessage class="body-2" :definition="translations.trustedPeriod">
        <template #period>
          <span class="body-2-bold">{{ periodText }}</span>
        </template>
      </FormattedMessage>

      <p v-if="displayCountry" class="body-2" data-allow-mismatch="text">
        {{ i18n(translations.basedIn, { country }) }}
        <Flag
          v-if="countryCode"
          aria-hidden="true"
          class="ml-2 inline-block align-baseline"
          :country-code
        />
      </p>
    </div>
  </RevCard>

  <!-- Desktop Card -->
  <RevCard class="mt-24 hidden items-center justify-start gap-8 p-16 md:flex">
    <div
      v-if="averageRate"
      class="flex items-center gap-4"
      :class="{ 'w-1/3': displayCountry, 'w-1/2': !displayCountry }"
    >
      <IconOfficialFilled size="24" />
      <FormattedMessage
        class="body-2"
        :definition="translations.approvedSeller"
      >
        <template #rating>
          <span class="body-2-bold">
            {{
              i18n(translations.sellerRating, {
                rating: averageRate?.toPrecision(2),
                maxRating: MAX_RATING,
              })
            }}
          </span>
        </template>
      </FormattedMessage>
    </div>

    <div
      class="flex items-center gap-4"
      :class="{ 'w-1/3': displayCountry, 'w-1/2': !displayCountry }"
    >
      <IconCalendar size="24" />
      <FormattedMessage class="body-2" :definition="translations.trustedPeriod">
        <template #period>
          <span class="body-2-bold">{{ periodText }}</span>
        </template>
      </FormattedMessage>
    </div>

    <div v-if="displayCountry" class="flex w-1/3 items-center gap-4">
      <Flag
        v-if="countryCode"
        aria-hidden="true"
        class="mr-4 inline-block align-baseline"
        :country-code
      />
      <p class="body-2">
        {{ i18n(translations.basedIn, { country }) }}
      </p>
    </div>
  </RevCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { IconCalendar } from '@ds/icons/IconCalendar'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'

import translations from './MainSellerInfo.translations'

const i18n = useI18n()
const MAX_RATING = 5
const COUNTRIES_WITH_FLAG: readonly MarketCountryCode[] = [
  MarketCountryCode.US,
  MarketCountryCode.AU,
  MarketCountryCode.JP,
]

const {
  country = undefined,
  countryCode = undefined,
  averageRate = undefined,
  deltaAged,
} = defineProps<{
  country?: string
  countryCode?: MarketCountryCode
  averageRate?: number
  deltaAged: number
}>()

const displayCountry = computed(() => {
  if (!countryCode) return false

  return COUNTRIES_WITH_FLAG.includes(countryCode)
})

const periodText = computed(() => {
  if (!deltaAged) return ''
  if (deltaAged >= 12) {
    return i18n(translations.years, { years: Math.floor(deltaAged / 12) })
  }

  return i18n(translations.months, { months: deltaAged % 12 })
})
</script>
